import { applyPeriodShade } from "./PeriodShadeStyle";
import { getHsToken, clearHsToken, handleSessionExpired } from "./hsAuth";

// Client HappySchool không chạm Firebase trực tiếp — mọi thao tác đi qua
// happyschool-api (kiến trúc client -> api), khác với quy ước của quiz3.
// Đăng nhập KHÔNG còn qua Firebase Auth — token tự ký của happyschool-api,
// xem hsAuth.js.
const HAPPYSCHOOL_API_BASE = process.env.REACT_APP_HAPPYSCHOOL_API_BASE || "http://localhost:4001";
// Kênh gọi API: web gửi "web"; client không gửi header này (app Android) được server coi là android.
const CHANNEL = { "X-Channel": "web" };

export const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
export const SESSIONS = [
  { key: "S", label: "Sáng", periods: [0, 1, 2, 3, 4] },
  { key: "C", label: "Chiều", periods: [5, 6, 7, 8, 9] },
];

async function authedFetch(path, options = {}) {
  const token = getHsToken();
  if (!token) throw new Error("Bạn cần đăng nhập trước.");
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}${path}`, {
    ...options,
    headers: {
      ...CHANNEL,
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && data.mustChangePassword) {
    // Tài khoản bị buộc đổi mật khẩu: phiên cũ hết hiệu lực -> đăng xuất, về trang đăng nhập để đổi mật khẩu.
    clearHsToken();
    try { window.alert(data.error || "Bạn cần đổi mật khẩu. Vui lòng đăng nhập lại và đổi mật khẩu."); } catch (_) {}
    window.location.assign("/login");
    throw new Error(data.error || "Cần đổi mật khẩu.");
  }
  if (res.status === 401) {
    handleSessionExpired();
    throw new Error(data.error || "Phiên đăng nhập đã hết hạn.");
  }
  if (!res.ok) throw new Error(data.error || `Lỗi ${res.status}`);
  return data;
}

// ---- Trường ----
export const listMySchools = () => authedFetch("/api/schools");

// ---- Tham gia trường (ra nhập) + xác thực + cổng học sinh ----
export const listSchoolsDirectory = () => authedFetch("/api/schools-directory");
export const getMyMembership = (schoolId) => authedFetch(`/api/schools/${schoolId}/my-membership`);
export const joinSchool = (schoolId, role, { name, dob, cccd, className } = {}) =>
  authedFetch(`/api/schools/${schoolId}/join`, { method: "POST", body: JSON.stringify({ role, name, dob, cccd, className }) });
export const listJoinRequests = (schoolId) => authedFetch(`/api/schools/${schoolId}/join-requests`);
export const approveJoinRequest = (schoolId, reqUid, data = {}) =>
  authedFetch(`/api/schools/${schoolId}/join-requests/${reqUid}/approve`, { method: "POST", body: JSON.stringify(data) });
export const rejectJoinRequest = (schoolId, reqUid) =>
  authedFetch(`/api/schools/${schoolId}/join-requests/${reqUid}/reject`, { method: "POST" });
export const listUserAccounts = (schoolId) => authedFetch(`/api/schools/${schoolId}/user-accounts`);
// Nhiều vai trò cho 1 tài khoản: roles = ["admin","teacher",...]; [] = gỡ khỏi trường.
export const setUserRoles = (schoolId, uid, roles) =>
  authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/role`, { method: "PUT", body: JSON.stringify({ roles }) });
export const setUserRole = (schoolId, uid, role) =>
  authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/role`, { method: "PUT", body: JSON.stringify({ role }) });
export const deleteUserAccount = (schoolId, uid) =>
  authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}`, { method: "DELETE" });
export const resetUserPassword = (schoolId, uid, password) =>
  authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/reset-password`, { method: "PUT", body: JSON.stringify({ password }) });
export const getMyStudentInfo = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/student`);
export const linkMyStudent = (schoolId, className, dob) =>
  authedFetch(`/api/schools/${schoolId}/me/link-student`, { method: "POST", body: JSON.stringify({ className, dob }) });
export const getMyTimetable = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/timetable`);
export const createSchool = (name) => authedFetch("/api/schools", { method: "POST", body: JSON.stringify({ name }) });

// ---- Nhân sự (staff) — "Giáo viên" chỉ là view lọc theo Khối ----
// Lịch bận (busy) trả về LUÔN gắn với 1 tuần TKB — truyền weekId để lấy đúng.
export const listStaff = (schoolId, { department, weekId, q } = {}) => {
  const qs = new URLSearchParams();
  if (department) qs.set("department", department);
  if (weekId) qs.set("weekId", weekId);
  if (q) qs.set("q", q);
  const qsStr = qs.toString();
  return authedFetch(`/api/schools/${schoolId}/staff${qsStr ? `?${qsStr}` : ""}`);
};
export const createStaff = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/staff`, { method: "POST", body: JSON.stringify(data) });
export const updateStaff = (schoolId, staffId, data) =>
  authedFetch(`/api/schools/${schoolId}/staff/${encodeURIComponent(staffId)}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteStaff = (schoolId, staffId) =>
  authedFetch(`/api/schools/${schoolId}/staff/${encodeURIComponent(staffId)}`, { method: "DELETE" });
// Hồ sơ của chính mình (GV/cán bộ chỉ sửa được thông tin của mình).
export const getMyStaff = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/staff`);
export const updateMyStaff = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/me/staff`, { method: "PUT", body: JSON.stringify(data) });
// Xác thực thông tin (Họ tên + CCCD + Ngày sinh) -> mở khoá xem/sửa hồ sơ.
export const verifyMyInfo = (schoolId, { name, cccd, dob }) =>
  authedFetch(`/api/schools/${schoolId}/me/verify-info`, { method: "POST", body: JSON.stringify({ name, cccd, dob }) });
// Thông tin xác thực KHÔNG khớp hồ sơ -> lưu tạm, chờ owner/admin duyệt.
export const listVerifyRequests = (schoolId) => authedFetch(`/api/schools/${schoolId}/verify-requests`);
export const approveVerifyRequest = (schoolId, uid) =>
  authedFetch(`/api/schools/${schoolId}/verify-requests/${uid}/approve`, { method: "POST" });
export const rejectVerifyRequest = (schoolId, uid) =>
  authedFetch(`/api/schools/${schoolId}/verify-requests/${uid}/reject`, { method: "POST" });
// GV tự khai báo lịch bận theo từng tuần TKB.
export const getMyBusyWeeks = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/busy-weeks`);
export const getMyBusy = (schoolId, weekId) => authedFetch(`/api/schools/${schoolId}/me/busy/${encodeURIComponent(weekId)}`);
export const setMyBusy = (schoolId, weekId, busy) =>
  authedFetch(`/api/schools/${schoolId}/me/busy/${encodeURIComponent(weekId)}`, { method: "PUT", body: JSON.stringify({ busy }) });
export const updateStaffBusy = (schoolId, staffId, busy, weekId) =>
  authedFetch(`/api/schools/${schoolId}/staff/${encodeURIComponent(staffId)}/busy?weekId=${encodeURIComponent(weekId)}`, {
    method: "PUT",
    body: JSON.stringify({ busy }),
  });

// Bật/tắt "gom lịch dạy" cho GV nhà xa (dồn vào ít buổi nhất).
export const updateStaffCompact = (schoolId, staffId, compact) =>
  authedFetch(`/api/schools/${schoolId}/staff/${encodeURIComponent(staffId)}/compact`, {
    method: "PUT",
    body: JSON.stringify({ compact }),
  });
// Bật/tắt "GV nội trú/ở gần trường" — miễn phạt "đi 1 tiết rồi về" khi xếp TKB.
export const updateStaffResident = (schoolId, staffId, resident) =>
  authedFetch(`/api/schools/${schoolId}/staff/${encodeURIComponent(staffId)}/resident`, {
    method: "PUT",
    body: JSON.stringify({ resident }),
  });

export const TEACHER_DEPARTMENT = "Giáo viên THPT";

// ---- Môn học ----
export const listSubjects = (schoolId) => authedFetch(`/api/schools/${schoolId}/subjects`);
export const createSubject = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/subjects`, { method: "POST", body: JSON.stringify(data) });
export const updateSubject = (schoolId, subjectId, data) =>
  authedFetch(`/api/schools/${schoolId}/subjects/${subjectId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteSubject = (schoolId, subjectId) =>
  authedFetch(`/api/schools/${schoolId}/subjects/${subjectId}`, { method: "DELETE" });

// ---- Lớp học & gán môn ----
export const listClasses = (schoolId) => authedFetch(`/api/schools/${schoolId}/classes`);
// Mọi thành viên trường xem được — server chỉ trả tên lớp, GVCN + SĐT, lớp trưởng/phó.
export const getHomeroomTeachers = (schoolId) => authedFetch(`/api/schools/${schoolId}/homeroom-teachers`);
// data: string (tên lớp) hoặc object { name, classType, combinationId, transport, note }
export const createClass = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/classes`, {
    method: "POST",
    body: JSON.stringify(typeof data === "string" ? { name: data } : data),
  });
export const updateClass = (schoolId, classId, data) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}`, {
    method: "PUT",
    body: JSON.stringify(typeof data === "string" ? { name: data } : data),
  });
export const deleteClass = (schoolId, classId) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}`, { method: "DELETE" });

// ---- Đăng nhập bằng SĐT + mật khẩu (không cần token) ----
export const phoneLogin = async (phone, password, dob) => {
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/auth/phone-login`, {
    method: "POST",
    headers: { ...CHANNEL, "Content-Type": "application/json" },
    body: JSON.stringify({ phone, password, ...(dob ? { dob } : {}) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const err = new Error(data.error || `Lỗi ${res.status}`); err.mustChangePassword = !!data.mustChangePassword; err.needDob = !!data.needDob; throw err; } // mustChangePassword: mật khẩu yếu -> phải đổi trước khi đăng nhập
  return data; // { token, name, schoolId, mustChangePassword }
};
export const phoneRegister = async (phone, password, name) => {
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/auth/phone-register`, {
    method: "POST",
    headers: { ...CHANNEL, "Content-Type": "application/json" },
    body: JSON.stringify({ phone, password, name }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Lỗi ${res.status}`);
  return data; // { token, name }
};
// Đổi mật khẩu khi đã đăng nhập (dùng token) — không cần nhập lại SĐT.
export const changeMyPassword = (oldPassword, newPassword) =>
  authedFetch(`/api/auth/me/change-password`, { method: "POST", body: JSON.stringify({ oldPassword, newPassword }) });
export const phoneChangePassword = async (phone, oldPassword, newPassword, dob) => {
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/auth/phone-change-password`, {
    method: "POST",
    headers: { ...CHANNEL, "Content-Type": "application/json" },
    body: JSON.stringify({ phone, oldPassword, newPassword, ...(dob ? { dob } : {}) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const err = new Error(data.error || `Lỗi ${res.status}`); err.needDob = !!data.needDob; throw err; }
  return data;
};
// Xoá tài khoản: xác thực lại bằng SĐT + mật khẩu (giống đăng nhập), không
// cần token — dùng được từ trang công khai /deleteaccount.
export const phoneDeleteAccount = async (phone, password) => {
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/auth/phone-delete-account`, {
    method: "POST",
    headers: { ...CHANNEL, "Content-Type": "application/json" },
    body: JSON.stringify({ phone, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Lỗi ${res.status}`);
  return data;
};
// Xoá tài khoản Google — gọi NGAY SAU KHI vừa signInWithGoogle() lại trên
// trang /deleteaccount (xác thực = vừa đăng nhập lại thành công), dùng
// idToken của phiên đăng nhập đó (auth.currentUser tại thời điểm gọi).
export const deleteMyAccount = () => authedFetch("/api/auth/me/delete-account", { method: "POST" });

// ---- Dashboard ----
export const getDashboard = (schoolId) => authedFetch(`/api/schools/${schoolId}/dashboard`);

// ---- Học sinh ----
export const getStudentCounts = (schoolId, { activeOnly = false } = {}) => authedFetch(`/api/schools/${schoolId}/students-counts${activeOnly ? "?active=1" : ""}`);
export const searchStudents = (schoolId, { name, personalId, className } = {}) => {
  const qs = new URLSearchParams();
  if (name) qs.set("name", name);
  if (personalId) qs.set("personalId", personalId);
  if (className) qs.set("class", className);
  return authedFetch(`/api/schools/${schoolId}/students-search?${qs.toString()}`);
};
export const listStudents = (schoolId, classId) => authedFetch(`/api/schools/${schoolId}/students/${classId}`);
export const getStudentsCanEdit = (schoolId, classId) => authedFetch(`/api/schools/${schoolId}/students-can-edit/${classId}`);
export const createStudent = (schoolId, classId, data) =>
  authedFetch(`/api/schools/${schoolId}/students/${classId}`, { method: "POST", body: JSON.stringify(data) });
export const updateStudent = (schoolId, classId, studentId, data) =>
  authedFetch(`/api/schools/${schoolId}/students/${classId}/${studentId}`, { method: "PUT", body: JSON.stringify(data) });
export const moveStudent = (schoolId, classId, studentId, toClassId) =>
  authedFetch(`/api/schools/${schoolId}/students/${classId}/${studentId}/move`, { method: "POST", body: JSON.stringify({ toClassId }) });
export const deleteStudent = (schoolId, classId, studentId) =>
  authedFetch(`/api/schools/${schoolId}/students/${classId}/${studentId}`, { method: "DELETE" });

// ---- Tổ hợp bộ môn ----
export const listSubjectCombinations = (schoolId) => authedFetch(`/api/schools/${schoolId}/subject-combinations`);
export const createSubjectCombination = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/subject-combinations`, { method: "POST", body: JSON.stringify(data) });
export const updateSubjectCombination = (schoolId, comboId, data) =>
  authedFetch(`/api/schools/${schoolId}/subject-combinations/${comboId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteSubjectCombination = (schoolId, comboId) =>
  authedFetch(`/api/schools/${schoolId}/subject-combinations/${comboId}`, { method: "DELETE" });
export const setClassSubject = (schoolId, classId, subjectId, data) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}/subjects/${subjectId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
export const deleteClassSubject = (schoolId, classId, subjectId) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}/subjects/${subjectId}`, { method: "DELETE" });

// ---- Phân bổ học văn hoá-nghề theo lớp ----
export const getClassDaySchedule = (schoolId) => authedFetch(`/api/schools/${schoolId}/class-day-schedule`);
export const setClassDaySchedule = (schoolId, classId, data) =>
  authedFetch(`/api/schools/${schoolId}/class-day-schedule/${classId}`, { method: "PUT", body: JSON.stringify(data) });

// ---- Giờ học (giờ đồng hồ từng tiết) ----
// Kèm áp luôn màu nền buổi chiều đã lưu cho trường (mọi bảng TKB đều gọi hàm này nên tự có màu).
export const getPeriodTimes = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/period-times`).then((data) => {
    applyPeriodShade(data && data.shade && data.shade.endOfSession);
    return data;
  });
export const setPeriodShade = (schoolId, endOfSession) =>
  authedFetch(`/api/schools/${schoolId}/period-shade`, { method: "PUT", body: JSON.stringify({ endOfSession }) });
export const setPeriodTimes = (schoolId, sessions) =>
  authedFetch(`/api/schools/${schoolId}/period-times`, { method: "PUT", body: JSON.stringify({ sessions }) });

// ---- Số tiết/tuần quy định (Chọn/Thường × Khối × Môn, theo Học kỳ) ----
export const getPeriodRules = (schoolId, weekId) => authedFetch(`/api/schools/${schoolId}/period-rules/${weekId}`);
export const getPeriodRulesDefault = (schoolId) => authedFetch(`/api/schools/${schoolId}/period-rules-default`);
export const setPeriodRules = (schoolId, weekId, rules) =>
  authedFetch(`/api/schools/${schoolId}/period-rules/${weekId}`, { method: "PUT", body: JSON.stringify({ rules }) });
export const applyPeriodRules = (schoolId, weekId, hocky, mode = "total") =>
  authedFetch(`/api/schools/${schoolId}/period-rules/${weekId}/apply`, { method: "POST", body: JSON.stringify({ hocky, mode }) });

// ---- Tuần TKB ----
export const listTimetableWeeks = (schoolId) => authedFetch(`/api/schools/${schoolId}/timetable-weeks`);
export const createTimetableWeek = (schoolId, startDate, endDate, khois = [], excludeFromChamCong = false, name = "", copyFromWeekId = "", copyItems = []) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks`, { method: "POST", body: JSON.stringify({ startDate, endDate, khois, excludeFromChamCong, name, copyFromWeekId, copyItems }) });
// Số dữ liệu của từng mục cấu hình con của 1 tuần (để hiện "có dữ liệu/trống" khi chọn tuần nguồn sao chép).
export const getWeekConfigSummary = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks/${encodeURIComponent(weekId)}/config-summary`);
// Mở/đóng cho phép GV khai báo lịch bận của 1 tuần (owner/admin).
export const setWeekBusyOpen = (schoolId, weekId, busyOpen) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks/${encodeURIComponent(weekId)}/busy-open`, { method: "PUT", body: JSON.stringify({ busyOpen }) });
// Đánh dấu tuần "Báo giảng (ảo)" — Chấm công sẽ bỏ hẳn tuần này.
export const setWeekExcludeChamCong = (schoolId, weekId, excludeFromChamCong) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks/${encodeURIComponent(weekId)}/exclude-chamcong`, { method: "PUT", body: JSON.stringify({ excludeFromChamCong }) });
// Đặt/đổi tên riêng cho 1 tuần (để phân biệt các tuần trùng ngày, vd bản sao).
// name rỗng = xoá tên, quay lại hiển thị theo ngày như mặc định.
export const renameTimetableWeek = (schoolId, weekId, name) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks/${encodeURIComponent(weekId)}/name`, { method: "PUT", body: JSON.stringify({ name }) });
// Xoá tuần TKB (soft-delete -> vào thùng rác, khôi phục được).
export const deleteTimetableWeek = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-weeks/${encodeURIComponent(weekId)}`, { method: "DELETE" });
export const listTrashedTimetableWeeks = (schoolId) => authedFetch(`/api/schools/${schoolId}/trash/timetable-weeks`);
export const restoreTimetableWeek = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/trash/timetable-weeks/${encodeURIComponent(weekId)}/restore`, { method: "POST" });
export const purgeTimetableWeek = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/trash/timetable-weeks/${encodeURIComponent(weekId)}`, { method: "DELETE" });

// ---- Bảng dữ liệu tuỳ biến (Ô tô đưa đón, Ăn bán trú, ...) ----
export const getChamCongGvcnNghe = (schoolId, monthKey, names) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/gvcn-nghe?names=${encodeURIComponent((names || []).join("|"))}`);
export const getCongDaoTao = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/cong-dao-tao`);
export const saveCongDaoTao = (schoolId, monthKey, payload) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/cong-dao-tao`, { method: "PUT", body: JSON.stringify(payload) });
export const getBangLuong = (schoolId, monthKey, kind) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/bang/${kind}`);
export const saveBangLuong = (schoolId, monthKey, kind, payload) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/bang/${kind}`, { method: "PUT", body: JSON.stringify(payload) });
// Lương xe theo tháng: workbook nhiều sheet (ô + công thức).
export const getLuongXe = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/luong-xe`);
export const saveLuongXe = (schoolId, monthKey, payload) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/luong-xe`, { method: "PUT", body: JSON.stringify(payload) });
export const getPcKiemXe = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/pc-kiem-xe`);
export const savePcKiemXe = (schoolId, monthKey, hoten, soNgay) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/pc-kiem-xe`, { method: "PUT", body: JSON.stringify({ hoten, soNgay }) });
// Danh sách người kiểm xe (lưu database, dùng chung mọi tháng): action "add" | "remove".
export const savePcKiemXeDs = (schoolId, hoten, action) =>
  authedFetch(`/api/schools/${schoolId}/pc-kiem-xe-ds`, { method: "PUT", body: JSON.stringify({ hoten, action }) });
export const listPasswordBackups = (schoolId) => authedFetch(`/api/schools/${schoolId}/user-accounts/password-backups`);
export const createPasswordBackup = (schoolId, note) => authedFetch(`/api/schools/${schoolId}/user-accounts/password-backups`, { method: "POST", body: JSON.stringify({ note }) });
export const getPasswordBackup = (schoolId, id) => authedFetch(`/api/schools/${schoolId}/user-accounts/password-backups/${id}`);
export const restorePasswordBackup = (schoolId, id, data) => authedFetch(`/api/schools/${schoolId}/user-accounts/password-backups/${id}/restore`, { method: "POST", body: JSON.stringify(data) });
export const deletePasswordBackup = (schoolId, id) => authedFetch(`/api/schools/${schoolId}/user-accounts/password-backups/${id}`, { method: "DELETE" });
export const backupOnePassword = (schoolId, uid, note) => authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/password-backup`, { method: "POST", body: JSON.stringify({ note }) });
export const listOnePasswordPoints = (schoolId, uid) => authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/password-points`);
export const restoreOnePassword = (schoolId, uid, pointId) => authedFetch(`/api/schools/${schoolId}/user-accounts/${uid}/password-restore`, { method: "POST", body: JSON.stringify({ pointId }) });
export const getCustomTable = (schoolId, tableKey) =>
  authedFetch(`/api/schools/${schoolId}/table/${tableKey}`);
export const saveCustomTable = (schoolId, tableKey, data) =>
  authedFetch(`/api/schools/${schoolId}/table/${tableKey}`, { method: "PUT", body: JSON.stringify(data) });
export const publishCustomTable = (schoolId, tableKey) =>
  authedFetch(`/api/schools/${schoolId}/table/${tableKey}/publish`, { method: "PUT" });
export const getMyTable = (schoolId, tableKey) =>
  authedFetch(`/api/schools/${schoolId}/me/table/${tableKey}`);
export const getMyChamCongSummary = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/me/chamcong-summary`);
export const getMyThuNhapSummary = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/me/thunhap-summary`);
export const getPublishLog = (schoolId, { kind = "", weekId = "", monthKey = "", limit = 200 } = {}) =>
  authedFetch(`/api/schools/${schoolId}/publish-log?${new URLSearchParams({ ...(kind ? { kind } : {}), ...(weekId ? { weekId } : {}), ...(monthKey ? { monthKey } : {}), limit: String(limit) }).toString()}`);
export const putChinhSachLuong = (schoolId, staffId, body) =>
  authedFetch(`/api/schools/${schoolId}/chinh-sach-luong/${staffId}`, { method: "PUT", body: JSON.stringify(body) });
export const putChinhSachCauHinh = (schoolId, body) =>
  authedFetch(`/api/schools/${schoolId}/chinh-sach-luong-cau-hinh`, { method: "PUT", body: JSON.stringify(body) });
export const getChinhSachLuong = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chinh-sach-luong?${new URLSearchParams({ monthKey }).toString()}`);
export const getChamCongThangPublish = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang-publish`);
export const setChamCongThangPublish = (schoolId, monthKey, published) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/publish`, { method: "PUT", body: JSON.stringify({ published }) });
export const getChamCongNghe = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/nghe`);
export const getNgheXangXe = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/nghe-xang-xe`);
export const saveNgheXangXe = (schoolId, monthKey, hoten, soTien) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/nghe-xang-xe`, { method: "PUT", body: JSON.stringify({ hoten, soTien }) });
export const saveTreoLuong = (schoolId, monthKey, names) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/treo-luong`, { method: "PUT", body: JSON.stringify({ names }) });
export const getChamCongThangThuNhap = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/thunhap`);
// "Chấm công theo TKB" — tự tính từ bản TKB đã công bố, không qua bảng gõ tay.
export const getChamCongThangTuTKB = (schoolId, monthKey) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/tu-tkb`);
// Ghi điều chỉnh tay (+/- tiết, ghi chú) cho 1 GV ở "Chấm công theo TKB" (dialog nhấn đúp).
export const saveChamCongTuTKBAdjust = (schoolId, monthKey, staffId, data) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/tu-tkb/${staffId}/adjust`, { method: "PUT", body: JSON.stringify(data) });
// Gộp tiết tháng monthKey của 1 GV vào tháng liền trước (gop=true) / bỏ gộp (gop=false).
export const setGopTuTkb = (schoolId, monthKey, staffId, gop) =>
  authedFetch(`/api/schools/${schoolId}/chamcong-thang/${monthKey}/tu-tkb/${staffId}/gop`, { method: "PUT", body: JSON.stringify({ gop }) });
export const getMyKhaoSatTongHop = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/me/khao-sat/tonghop`);
export const getKhaoSatTongHop = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/khao-sat/tonghop`);

// ---- Cấu hình theo tuần TKB (chế độ môn học đã áp dụng...) ----
export const getWeekConfig = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/week-config/${weekId}`);
export const setWeekConfig = (schoolId, weekId, data) =>
  authedFetch(`/api/schools/${schoolId}/week-config/${weekId}`, { method: "PUT", body: JSON.stringify(data) });
// Cấu hình môn học theo tuần (kho riêng, không đụng Phân công chuyên môn).
// plan = { [classId]: { [subjectId]: số tiết } }.
export const getWeekSubjectPlan = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/week-subject-plan/${weekId}`);
export const setWeekSubjectPlan = (schoolId, weekId, plan) =>
  authedFetch(`/api/schools/${schoolId}/week-subject-plan/${weekId}`, { method: "PUT", body: JSON.stringify({ plan }) });
// Tiết liền khối/buổi (blockSize) riêng theo tuần + theo (lớp, môn) — ưu tiên
// cao nhất lúc xếp lịch, mặc định copy từ "Môn học" nhưng sửa ở đây không
// đụng cấu hình môn chung. plan = { [classId]: { [subjectId]: blockSize } }.
export const getWeekBlockSize = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/week-block-size/${weekId}`);
export const setWeekBlockSize = (schoolId, weekId, plan) =>
  authedFetch(`/api/schools/${schoolId}/week-block-size/${weekId}`, { method: "PUT", body: JSON.stringify({ plan }) });
// Tối đa tiết/ngày cho 1 môn, riêng theo tuần + theo (lớp, môn) — ràng buộc
// cứng lúc xếp lịch, mặc định 6. plan = { [classId]: { [subjectId]: maxPerDay } }.
export const getWeekMaxPerDay = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/week-max-per-day/${weekId}`);
export const setWeekMaxPerDay = (schoolId, weekId, plan) =>
  authedFetch(`/api/schools/${schoolId}/week-max-per-day/${weekId}`, { method: "PUT", body: JSON.stringify({ plan }) });

// ---- Điểm danh & Điểm kiểm tra (theo lớp) ----
export const listAttendance = (schoolId, classId) =>
  authedFetch(`/api/schools/${schoolId}/attendance/${classId}`);
export const getAttendance = (schoolId, classId, date, slot) =>
  authedFetch(`/api/schools/${schoolId}/attendance/${classId}/${date}/${slot}`);
export const setAttendance = (schoolId, classId, date, slot, records) =>
  authedFetch(`/api/schools/${schoolId}/attendance/${classId}/${date}/${slot}`, { method: "PUT", body: JSON.stringify({ records }) });
export const listExams = (schoolId, classId) =>
  authedFetch(`/api/schools/${schoolId}/scores/${classId}`);
export const listQuiz3Quizzes = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/quiz3-quizzes`);
export const getExam = (schoolId, classId, examId) =>
  authedFetch(`/api/schools/${schoolId}/scores/${classId}/${examId}`);
export const saveExam = (schoolId, classId, examId, data) =>
  authedFetch(`/api/schools/${schoolId}/scores/${classId}/${examId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteExam = (schoolId, classId, examId) =>
  authedFetch(`/api/schools/${schoolId}/scores/${classId}/${examId}`, { method: "DELETE" });
export const setExamStatus = (schoolId, classId, examId, status) =>
  authedFetch(`/api/schools/${schoolId}/scores/${classId}/${examId}/status`, { method: "PUT", body: JSON.stringify({ status }) });

// ---- TKB nghề (lịch học nghề chi tiết theo tuần) ----
export const getVocationalSchedule = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vocational-schedule/${weekId}`);

// ---- Lịch phân phối nghề-văn hoá (sinh từ TKB nghề) ----
// ---- Phòng học + phân phòng học theo tuần ----
export const listRooms = (schoolId) => authedFetch(`/api/schools/${schoolId}/rooms`);
export const saveRooms = (schoolId, rooms) =>
  authedFetch(`/api/schools/${schoolId}/rooms`, { method: "PUT", body: JSON.stringify({ rooms }) });
export const getRoomAssignment = (schoolId, weekId) => authedFetch(`/api/schools/${schoolId}/room-assignment/${weekId}`);
export const saveRoomAssignment = (schoolId, weekId, cells) =>
  authedFetch(`/api/schools/${schoolId}/room-assignment/${weekId}`, { method: "PUT", body: JSON.stringify({ cells }) });
export const getVanhoaDistribution = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution/${weekId}`);
export const generateVanhoaDistribution = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution/${weekId}/generate`, { method: "POST" });
export const getVanhoaDistributionDraft = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution-draft/${weekId}`);
export const setVanhoaDistributionDraft = (schoolId, weekId, record) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution-draft/${weekId}`, { method: "PUT", body: JSON.stringify(record) });
export const applyVanhoaDistributionDraft = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution/${weekId}/apply-draft`, { method: "POST" });
export const listVanhoaDistributionHistory = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution/${weekId}/history`);
export const setVanhoaDistributionLock = (schoolId, weekId, locked) =>
  authedFetch(`/api/schools/${schoolId}/vanhoa-distribution/${weekId}/lock`, { method: "PUT", body: JSON.stringify({ locked }) });

// ---- Xếp thời khoá biểu ----
export const startTimetableRun = (schoolId, weekId, freeze) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs`, {
    method: "POST",
    body: JSON.stringify(freeze ? { weekId, freezeDaysFromRunId: freeze.runId, freezeDays: freeze.days } : { weekId }),
  });
export const getTimetableRun = (schoolId, runId, opts) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}${opts && opts.liveShortfall ? "?liveShortfall=1" : ""}`);
export const getLatestTimetableRun = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/latest`);
export const listTimetableRuns = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs${weekId ? `?weekId=${encodeURIComponent(weekId)}` : ""}`);
// Bảng tra nhanh tuần nào đang công bố bản nào — đọc 1 lần, không phải quét
// từng tuần (dùng cho trang "TKB của tôi" tìm bản công bố gần nhất).
export const getPublishedRuns = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/published-runs`);
// Chỉ bản công bố mới nhất (null nếu chưa có) — BGH xem TKB.
export const getLatestPublishedRun = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/published-runs/latest`);
// Sửa tay 1 ô: ô đã có môn thì chỉ đổi GV (giữ nguyên môn); ô đang TRỐNG thì
// phải truyền kèm subjectId (chọn cả môn lẫn GV cùng lúc) — ghi vào NHÁP,
// server kiểm tra bận/trùng giờ. Phải gọi approveTimetableRunDraft mới
// thành version mới.
export const updateTimetableRunCell = (schoolId, runId, { classId, day, period, teacherId, subjectId }) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/cell`, {
    method: "PUT",
    body: JSON.stringify({ classId, day, period, teacherId, subjectId }),
  });
// Admin: xử lý 1 ô TKB khi GV vắng — mode "replace" (dạy thay) | "supervise" (trông lớp thay) | "clear" (để trống GV),
// kèm lý do. Áp dụng TRỰC TIẾP vào bản đang xem (không qua nháp), tự ghi chú lên TKB giáo viên.
export const substituteRunCell = (schoolId, runId, { classId, day, period, mode, newTeacherId, newSubjectId, reason }) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/cell-substitute`, {
    method: "POST",
    body: JSON.stringify({ classId, day, period, mode, newTeacherId, newSubjectId, reason }),
  });
// Admin: điền 1 Ô TRỐNG của giáo viên bằng (lớp, môn) — ghi thẳng vào bản đang xem, không ghi ô xếp tay.
export const assignRunCell = (schoolId, runId, { teacherId, classId, subjectId, day, period, reason, overwrite }) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/cell-assign`, {
    method: "POST",
    body: JSON.stringify({ teacherId, classId, subjectId, day, period, reason, overwrite }),
  });
// (24/10/2026) GV đổi tiết trong 1 lớp: hoán đổi 2 ô (from <-> to) của cùng lớp. Bản đã công bố -> {pending:true} chờ duyệt.
export const swapRunCells = (schoolId, runId, { classId, fromDay, fromPeriod, toDay, toPeriod, reason }) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/cell-swap`, {
    method: "POST",
    body: JSON.stringify({ classId, fromDay, fromPeriod, toDay, toPeriod, reason }),
  });
// (21/10/2026) Sửa TKB sau công bố phải DUYỆT: danh sách yêu cầu (status pending|approved|rejected) + duyệt/từ chối.
export const listPendingEdits = (schoolId, status = "pending", runId = "") =>
  authedFetch(`/api/schools/${schoolId}/pending-edits?status=${encodeURIComponent(status)}${runId ? `&runId=${encodeURIComponent(runId)}` : ""}`);
export const decidePendingEdit = (schoolId, runId, id, action, note = "") =>
  authedFetch(`/api/schools/${schoolId}/pending-edits/${runId}/${id}/${action}`, { method: "POST", body: JSON.stringify({ note }) });
export const getTimetableRunDraft = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/draft`);
export const discardTimetableRunDraft = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/draft`, { method: "DELETE" });
export const approveTimetableRunDraft = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/approve-draft`, { method: "POST" });
// Bước RIÊNG, chủ động bấm mới chạy (KHÔNG tự động lúc xếp): với mỗi lớp-môn
// còn thiếu tiết + còn ô trống, tìm 1 GV khác cùng môn cùng khối dạy thay,
// ưu tiên giữ nguyên 1 người xuyên suốt. Ghi vào NHÁP, phải Duyệt mới áp dụng.
export const fillKhoiSubstitute = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/fill-khoi-substitute`, { method: "POST" });
// Công bố 1 bản cho trang "TKB cho GV & Học sinh" — chỉ owner/admin gọi được.
// Công bố bản này tự động bỏ công bố các bản KHÁC cùng tuần.
// mode: "official" (chính thức) | "demo" (xem thử — không tính chấm công, sửa ô không cần duyệt).
export const publishTimetableRun = (schoolId, runId, mode = "official") =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/publish`, { method: "POST", body: JSON.stringify({ mode }) });
export const unpublishTimetableRun = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/unpublish`, { method: "POST" });
// "Case xếp tay" — ô (lớp,môn,ngày,tiết,GV) khoá cứng bền theo tuần, áp dụng
// mọi lần chạy SAT sau này cho tới khi xoá.
export const listManualLocks = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}`);
export const addManualLock = (schoolId, weekId, { classId, subjectId, day, period, teacherId, reason }) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}`, {
    method: "POST",
    body: JSON.stringify({ classId, subjectId, day, period, teacherId, reason }),
  });
// Xếp tay theo thứ tự ưu tiên GV: danh sách + lưu thứ tự + tự xếp tay 1 GV (CP-SAT).
export const getManualLockPriority = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}/priority`);
export const saveManualLockPriority = (schoolId, order, skip) =>
  authedFetch(`/api/schools/${schoolId}/manual-lock-priority`, { method: "PUT", body: JSON.stringify({ order, skip }) });
export const clearTeacherLocksWeek = (schoolId, weekId, teacherIds, minNeed, onlyKhoi12) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}/clear-teachers`, { method: "POST", body: JSON.stringify({ teacherIds, minNeed, onlyKhoi12: !!onlyKhoi12 }) });
export const autoLockTeacherWeek = (schoolId, weekId, body) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}/auto`, { method: "POST", body: JSON.stringify(body) });
export const deleteManualLock = (schoolId, weekId, lockId) =>
  authedFetch(`/api/schools/${schoolId}/manual-locks/${weekId}/${lockId}`, { method: "DELETE" });
// Ghi chú riêng theo từng ô (lớp-ngày-tiết) — độc lập môn/GV và độc lập bản
// chạy CP-SAT nào, bền theo tuần (vd "cô X trông lớp hộ" khi để trống tiết).
// runId: ghi chú RIÊNG của bản đó (mỗi bản 1 bộ ghi chú). Không truyền -> bản đang công bố của tuần.
export const getPeriodNotes = (schoolId, weekId, runId) =>
  authedFetch(`/api/schools/${schoolId}/period-notes/${weekId}${runId ? `?runId=${encodeURIComponent(runId)}` : ""}`);
export const setPeriodNote = (schoolId, weekId, { classId, day, period, note, teacherId, runId }) =>
  authedFetch(`/api/schools/${schoolId}/period-notes/${weekId}`, {
    method: "PUT",
    body: JSON.stringify({ classId, day, period, note, teacherId, runId }),
  });
// Điểm ưu tiên xếp môn (theo khối) — trả về đúng con số CP-SAT sẽ dùng nếu
// chạy xếp lịch ngay bây giờ (rank môn + độ khít GV từng lớp), không chạy CP-SAT.
export const getSubjectPriority = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/subject-priority/${weekId}`);
// Lịch bận TẤT CẢ GV trong tuần (dùng tô đỏ/xanh khi sửa TKB toàn trường).
export const getStaffBusyWeek = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/staff-busy-week/${weekId}`);
// Kiểm tra TRƯỚC KHI xếp: GV nào có nguy cơ kẹt lịch cấu trúc (kiểu "cô Vũ
// Thị Thủy" — lịch bận quá hẹp, nhiều lớp cùng tranh 1 buổi rảnh hiếm hoi).
export const getPccmPreflightCheck = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/pccm-preflight-check/${weekId}`);
// "GVCN có dạy bộ môn thì phải xếp dạy lớp mình chủ nhiệm" — không phụ thuộc
// tuần, chỉ soi PCCM + danh sách GVCN.
export const getPccmGvcnCheck = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/pccm-gvcn-check`);
// "Chấp nhận đổi" gợi ý từ Kiểm tra trước khi xếp — thêm GV vào pool dự
// phòng của (lớp, môn), KHÔNG đổi GV chính, có lưu vết lịch sử PCCM.
export const addPoolTeacher = (schoolId, classId, subjectId, teacherId) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}/subjects/${subjectId}/add-pool-teacher`, {
    method: "POST",
    body: JSON.stringify({ teacherId }),
  });
// Đổi GV chính (assignedTeacherId) sang 1 GV đang ở pool dự phòng — dùng khi
// "Case xếp tay" khoá đủ hết periodsPerWeek cho GV đó, có lưu vết lịch sử PCCM.
export const setAssignedTeacher = (schoolId, classId, subjectId, teacherId) =>
  authedFetch(`/api/schools/${schoolId}/classes/${classId}/subjects/${subjectId}/set-assigned-teacher`, {
    method: "POST",
    body: JSON.stringify({ teacherId }),
  });

// ---- Thu học phí (theo đợt) ----
export const listTuitionBatches = (schoolId) => authedFetch(`/api/schools/${schoolId}/tuition-batches`);
export const createTuitionBatch = (schoolId, name, note) =>
  authedFetch(`/api/schools/${schoolId}/tuition-batches`, { method: "POST", body: JSON.stringify({ name, note }) });
export const deleteTuitionBatch = (schoolId, batchId) =>
  authedFetch(`/api/schools/${schoolId}/tuition-batches/${batchId}`, { method: "DELETE" });
export const getTuitionRows = (schoolId, batchId) =>
  authedFetch(`/api/schools/${schoolId}/tuition-batches/${batchId}/rows`);
export const setTuitionRows = (schoolId, batchId, rows) =>
  authedFetch(`/api/schools/${schoolId}/tuition-batches/${batchId}/rows`, { method: "PUT", body: JSON.stringify({ rows }) });

// ---- Thanh toán lương (theo đợt) ----
export const listPayrollBatches = (schoolId) => authedFetch(`/api/schools/${schoolId}/payroll-batches`);
export const createPayrollBatch = (schoolId, name, note) =>
  authedFetch(`/api/schools/${schoolId}/payroll-batches`, { method: "POST", body: JSON.stringify({ name, note }) });
export const deletePayrollBatch = (schoolId, batchId) =>
  authedFetch(`/api/schools/${schoolId}/payroll-batches/${batchId}`, { method: "DELETE" });
export const getPayrollRows = (schoolId, batchId) =>
  authedFetch(`/api/schools/${schoolId}/payroll-batches/${batchId}/rows`);
export const setPayrollRows = (schoolId, batchId, rows) =>
  authedFetch(`/api/schools/${schoolId}/payroll-batches/${batchId}/rows`, { method: "PUT", body: JSON.stringify({ rows }) });
export const getMyPayroll = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/payroll`);
// GVCN: danh sách HS lớp chủ nhiệm + Phòng thi / SBD của kỳ thi khảo sát.
// Nhân sự Đào tạo... xem thu nhập của mình từ bảng Công Đào Tạo.
export const getMyCongDaoTao = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/cong-dao-tao`);
export const getThiKhaoSatLopCn = (schoolId, { examId, classId } = {}) =>
  authedFetch(`/api/schools/${schoolId}/thi-khao-sat-lop-cn?${new URLSearchParams({ ...(examId ? { examId } : {}), ...(classId ? { classId } : {}) }).toString()}`);
export const getPayrollForStaff = (schoolId, staffId) => authedFetch(`/api/schools/${schoolId}/payroll-for-staff/${staffId}`);

// ---- Soạn thảo PCCM (nháp) — độc lập hoàn toàn với dữ liệu PCCM đang dùng xếp TKB ----
export const getPccmDraft = (schoolId) => authedFetch(`/api/schools/${schoolId}/pccm-draft`);
export const setPccmDraft = (schoolId, data) =>
  authedFetch(`/api/schools/${schoolId}/pccm-draft`, { method: "PUT", body: JSON.stringify(data) });
export const applyPccmDraft = (schoolId) =>
  authedFetch(`/api/schools/${schoolId}/pccm-draft/apply`, { method: "POST" });

// ---- Lịch sử PCCM thật (chụp tự động mỗi khi Duyệt hoặc mỗi khi xếp TKB) ----
export const listPccmHistory = (schoolId) => authedFetch(`/api/schools/${schoolId}/pccm-history`);

// ---- Phân công chuyên môn RIÊNG theo tuần TKB — ghi đè từng môn/lớp cho
// đúng 1 tuần, không đụng PCCM chung (classSubjects) dùng cho mọi tuần khác. ----
export const getClassSubjectsByWeek = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/class-subjects-by-week/${encodeURIComponent(weekId)}`);
export const setClassSubjectByWeek = (schoolId, weekId, classId, subjectId, patch) =>
  authedFetch(`/api/schools/${schoolId}/class-subjects-by-week/${encodeURIComponent(weekId)}`, {
    method: "PUT",
    body: JSON.stringify({ classId, subjectId, patch }),
  });
export const clearClassSubjectByWeek = (schoolId, weekId, classId, subjectId) =>
  authedFetch(`/api/schools/${schoolId}/class-subjects-by-week/${encodeURIComponent(weekId)}`, {
    method: "PUT",
    body: JSON.stringify({ classId, subjectId, clear: true }),
  });
export const getPccmHistoryVersion = (schoolId, versionId) =>
  authedFetch(`/api/schools/${schoolId}/pccm-history/${versionId}`);

// ---- Lớp ảo (vd 12A9HN) — gộp HS nhiều lớp để quản lý danh sách, không đụng lớp gốc ----
export const listVirtualClasses = (schoolId) => authedFetch(`/api/schools/${schoolId}/virtual-classes`);
export const createVirtualClass = (schoolId, name) =>
  authedFetch(`/api/schools/${schoolId}/virtual-classes`, { method: "POST", body: JSON.stringify({ name }) });
export const deleteVirtualClass = (schoolId, vcId) =>
  authedFetch(`/api/schools/${schoolId}/virtual-classes/${vcId}`, { method: "DELETE" });
export const getVirtualClassMembers = (schoolId, vcId) =>
  authedFetch(`/api/schools/${schoolId}/virtual-classes/${vcId}/members`);
export const removeVirtualClassMember = (schoolId, vcId, studentId) =>
  authedFetch(`/api/schools/${schoolId}/virtual-classes/${vcId}/members/${studentId}`, { method: "DELETE" });
export const getMyVirtualClasses = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/virtual-classes`);
export const joinVirtualClass = (schoolId, vcId) =>
  authedFetch(`/api/schools/${schoolId}/me/virtual-classes/${vcId}/join`, { method: "POST" });
export const leaveVirtualClass = (schoolId, vcId) =>
  authedFetch(`/api/schools/${schoolId}/me/virtual-classes/${vcId}/join`, { method: "DELETE" });

// ---- Yêu cầu chức năng (định nghĩa chức năng mới + file Excel mẫu) ----
export const listFeatureRequests = (schoolId) => authedFetch(`/api/schools/${schoolId}/feature-requests`);
export const createFeatureRequest = (schoolId, { title, description, file }) =>
  authedFetch(`/api/schools/${schoolId}/feature-requests`, { method: "POST", body: JSON.stringify({ title, description, file }) });
export const getFeatureRequestFile = (schoolId, id) => authedFetch(`/api/schools/${schoolId}/feature-requests/${id}/file`);
export const setFeatureRequestStatus = (schoolId, id, status, adminNote) =>
  authedFetch(`/api/schools/${schoolId}/feature-requests/${id}/status`, { method: "PUT", body: JSON.stringify({ status, adminNote }) });
export const deleteFeatureRequest = (schoolId, id) =>
  authedFetch(`/api/schools/${schoolId}/feature-requests/${id}`, { method: "DELETE" });

// ---- Dạy thay: giáo viên bận/vắng theo ngày ----
export const getTeacherAbsences = (schoolId, date) =>
  authedFetch(`/api/schools/${schoolId}/teacher-absences/${date}`);
export const setTeacherAbsence = (schoolId, date, teacherId, note, periods) =>
  authedFetch(`/api/schools/${schoolId}/teacher-absences/${date}/${teacherId}`, { method: "PUT", body: JSON.stringify({ note, periods: periods || [] }) });
export const removeTeacherAbsence = (schoolId, date, teacherId) =>
  authedFetch(`/api/schools/${schoolId}/teacher-absences/${date}/${teacherId}`, { method: "DELETE" });
// Các ô TKB sửa SAU khi công bố của 1 bản (so với ảnh chụp lúc công bố), kèm GVCN.
export const getPostPublishChanges = (schoolId, runId) =>
  authedFetch(`/api/schools/${schoolId}/timetable-runs/${runId}/post-publish-changes`);
export const getSubstitutionLog = (schoolId, weekId) =>
  authedFetch(`/api/schools/${schoolId}/substitution-log/${weekId}`);

// GV/nhân sự tự xem "Lương theo tiết" của chính mình (đúng dòng của mình trong "Chấm công theo TKB", từng tháng).
export const getMyTuTkb = (schoolId) => authedFetch(`/api/schools/${schoolId}/me/tu-tkb`);

// ---- Thi khảo sát khối 12 (nhóm lớp, lịch thi, phòng thi, coi thi, điểm) ----
export const listThiKhaoSat = (schoolId) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat`);
export const createThiKhaoSat = (schoolId, name) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat`, { method: "POST", body: JSON.stringify({ name }) });
export const getThiKhaoSat = (schoolId, examId) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}`);
export const updateThiKhaoSat = (schoolId, examId, data) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteThiKhaoSat = (schoolId, examId) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}`, { method: "DELETE" });
export const autoRoomsThiKhaoSat = (schoolId, examId, roomSize) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/auto-rooms`, { method: "POST", body: JSON.stringify({ roomSize }) });
export const renumberSbdThiKhaoSat = (schoolId, examId, prefix) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/renumber-sbd`, { method: "POST", body: JSON.stringify({ prefix }) });
export const autoInvigilationThiKhaoSat = (schoolId, examId, perRoom) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/auto-invigilation`, { method: "POST", body: JSON.stringify({ perRoom }) });
export const saveScoresThiKhaoSat = (schoolId, examId, data) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/scores`, { method: "PUT", body: JSON.stringify(data) });
// Đáp án phiếu THPT 3 phần của 1 môn: { subjectKey, fileName, keys } (keys = null -> xoá).
export const saveAnswerKeyThiKhaoSat = (schoolId, examId, data) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/answer-key`, { method: "PUT", body: JSON.stringify(data) });
// Nhập điểm 1 môn theo SBD: { subjectKey, scores: { sbd: điểm } } -> { saved, missing }
export const saveScoresBySbdThiKhaoSat = (schoolId, examId, data) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/scores-by-sbd`, { method: "POST", body: JSON.stringify(data) });

// ---- Thi khảo sát: hồ sơ chấm phiếu từng thí sinh (đề, ảnh bài làm, đáp án) ----
export const getThiKhaoSatSheet = (schoolId, examId, { subjectKey, classId, studentId }) =>
  authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/sheet?${new URLSearchParams({ subjectKey, classId, studentId }).toString()}`);
export const getSheetsSummaryThiKhaoSat = (schoolId, examId, subjectKey) =>
  authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/sheets-summary?${new URLSearchParams({ subjectKey }).toString()}`);
export const saveSheetsThiKhaoSat = (schoolId, examId, data) => authedFetch(`/api/schools/${schoolId}/thi-khao-sat/${examId}/sheets`, { method: "POST", body: JSON.stringify(data) });
// File (PDF đề / ảnh bài làm) cần token -> tải về Blob rồi tạo object URL để hiển thị.
export async function fetchThiKhaoSatFile(schoolId, examId, fileId) {
  const token = getHsToken();
  if (!token) throw new Error("Bạn cần đăng nhập trước.");
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/schools/${schoolId}/thi-khao-sat/${examId}/files/${fileId}`, { headers: { ...CHANNEL, Authorization: `Bearer ${token}` } });
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Lỗi ${res.status}`); }
  return res.blob();
}
// Tải file lên: params = { kind: "de", subjectKey, maDe } | { kind: "bailam", subjectKey, sbd } | { kind: "scan", subjectKey, fileName } (PDF scan cả phòng)
export async function uploadThiKhaoSatFile(schoolId, examId, params, file) {
  const token = getHsToken();
  if (!token) throw new Error("Bạn cần đăng nhập trước.");
  const q = new URLSearchParams({ fileName: file.name || "", ...params }).toString(); // params.fileName (nếu có) ghi đè tên file
  const res = await fetch(`${HAPPYSCHOOL_API_BASE}/api/schools/${schoolId}/thi-khao-sat/${examId}/files?${q}`, {
    method: "PUT", headers: { ...CHANNEL, Authorization: `Bearer ${token}`, "Content-Type": file.type || "application/octet-stream" }, body: file,
  });
  const d = await res.json().catch(() => ({}));
  if (res.status === 401) handleSessionExpired();
  if (!res.ok) throw new Error(d.error || `Lỗi ${res.status}`);
  return d;
}
